import { REGION_KEYS, regionForCountryCode } from './store-locator-regions.js';

/**
 * Custom <store-locator> element.
 *
 * Shape:
 *   <store-locator data-...>
 *     <script type="application/json" data-stores>[...]</script>
 *     <script type="application/json" data-i18n>{...}</script>
 *     <div data-chrome></div>
 *   </store-locator>
 *
 * Cards are rendered client-side from the JSON payload. The map is
 * shown only on the `stores` tab (per spec).
 */

const TAB_TO_FLAG = {
  stores: 'store',
  online_stores: 'online_shop',
  showrooms: 'showroom',
  contract_dealers: 'contract_dealer',
};

const MAP_TABS = new Set(['stores']);

// Tabs that render the rich showroom card (description + image gallery +
// contact-details block) and use the non-map, in-flow list layout.
const SHOWROOM_TABS = new Set(['showrooms', 'contract_dealers']);

// Tabs that expose the search field. Showrooms / Contract Dealers have no
// search UI, so a query typed on another tab must not silently filter
// their results.
const SEARCH_TABS = new Set(['stores', 'online_stores']);

const DEFAULT_CENTER = { lat: 30, lng: 10 };
const DEFAULT_ZOOM = 2;
const FOCUS_ZOOM = 14;

/**
 * Panel footprint — kept in sync with the matching CSS values so the
 * map's visible focus stays in the uncovered portion of the map.
 *   Desktop: 490px white card pinned top-left, with 16px margin
 *            on each side → 522px of horizontal coverage.
 *   Mobile:  card slides up from the bottom, leaving the top 510px
 *            of the map area visible as the "focus" region.
 */
const PANEL_DESKTOP_WIDTH = 490;
const PANEL_DESKTOP_MARGIN = 16;
const PANEL_MOBILE_VISIBLE_TOP = 510;

/**
 * Custom pin SVG (33×41, fill #2F2B23 — Audo brand near-black).
 * The pin's tip is at (16.3, 40.3) so the marker anchor sits at the
 * bottom centre to keep the tip on the lat/lng point.
 */
const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="33" height="41" viewBox="0 0 33 41" fill="none"><path d="M16.3008 0.299805C25.1374 0.299805 32.3008 7.46324 32.3008 16.2998C32.3008 25.1364 16.3008 40.2998 16.3008 40.2998C16.3008 40.2998 0.300781 25.1364 0.300781 16.2998C0.300781 7.46324 7.46422 0.299805 16.3008 0.299805ZM16.3008 12.7998C14.3677 12.7998 12.8008 14.3668 12.8008 16.2998C12.8008 18.2328 14.3678 19.7998 16.3008 19.7998C18.2338 19.7998 19.8008 18.2328 19.8008 16.2998C19.8008 14.3668 18.2338 12.7998 16.3008 12.7998Z" fill="#2F2B23"/><path d="M16.3008 40.2998L16.0944 40.5176L16.3008 40.7131L16.5071 40.5176L16.3008 40.2998ZM16.3008 0.299805V0.599805C24.9717 0.599805 32.0008 7.62893 32.0008 16.2998H32.3008H32.6008C32.6008 7.29756 25.3031 -0.000195324 16.3008 -0.000195324V0.299805ZM32.3008 16.2998H32.0008C32.0008 18.43 31.0322 20.9791 29.5394 23.6306C28.0511 26.2739 26.0628 28.9829 24.0685 31.4237C22.0751 33.8634 20.0808 36.0287 18.5846 37.5845C17.8366 38.3622 17.2134 38.9872 16.7775 39.4175C16.5596 39.6326 16.3885 39.7991 16.272 39.9116C16.2138 39.9679 16.1692 40.0106 16.1393 40.0393C16.1243 40.0536 16.113 40.0643 16.1055 40.0715C16.1018 40.0751 16.0989 40.0778 16.0971 40.0795C16.0962 40.0804 16.0955 40.0811 16.095 40.0815C16.0948 40.0817 16.0947 40.0818 16.0945 40.0819C16.0944 40.082 16.0944 40.0821 16.3008 40.2998C16.5071 40.5176 16.5072 40.5175 16.5074 40.5173C16.5075 40.5172 16.5077 40.517 16.508 40.5167C16.5085 40.5162 16.5093 40.5155 16.5103 40.5145C16.5123 40.5126 16.5153 40.5098 16.5192 40.5061C16.5271 40.4986 16.5387 40.4875 16.5541 40.4728C16.5847 40.4435 16.63 40.4001 16.6889 40.3431C16.8068 40.2292 16.9795 40.0612 17.199 39.8445C17.6381 39.411 18.265 38.7823 19.017 38.0004C20.5207 36.4369 22.5265 34.2591 24.5331 31.8033C26.5388 29.3487 28.5505 26.61 30.0622 23.9249C31.5694 21.2479 32.6008 18.5879 32.6008 16.2998H32.3008ZM16.3008 40.2998C16.5071 40.0821 16.5071 40.082 16.507 40.0819C16.5069 40.0818 16.5068 40.0817 16.5065 40.0815C16.5061 40.0811 16.5054 40.0804 16.5045 40.0795C16.5026 40.0778 16.4998 40.0751 16.496 40.0715C16.4885 40.0643 16.4772 40.0536 16.4623 40.0393C16.4323 40.0106 16.3878 39.9679 16.3295 39.9116C16.2131 39.7991 16.042 39.6326 15.824 39.4175C15.3881 38.9872 14.765 38.3622 14.017 37.5845C12.5207 36.0287 10.5265 33.8634 8.53309 31.4237C6.53875 28.9829 4.55046 26.2739 3.0622 23.6306C1.5694 20.9791 0.600781 18.43 0.600781 16.2998H0.300781H0.000781238C0.000781238 18.5879 1.03216 21.2479 2.53937 23.9249C4.0511 26.61 6.06281 29.3487 8.06847 31.8033C10.0751 34.2591 12.0808 36.4369 13.5846 38.0004C14.3366 38.7823 14.9634 39.411 15.4025 39.8445C15.6221 40.0612 15.7947 40.2292 15.9126 40.3431C15.9716 40.4001 16.0169 40.4435 16.0475 40.4728C16.0628 40.4875 16.0745 40.4986 16.0823 40.5061C16.0863 40.5098 16.0892 40.5126 16.0913 40.5145C16.0923 40.5155 16.093 40.5162 16.0936 40.5167C16.0938 40.517 16.094 40.5172 16.0942 40.5173C16.0943 40.5175 16.0944 40.5176 16.3008 40.2998ZM0.300781 16.2998H0.600781C0.600781 7.62893 7.62991 0.599805 16.3008 0.599805V0.299805V-0.000195324C7.29854 -0.000195324 0.000781238 7.29756 0.000781238 16.2998H0.300781ZM16.3008 12.7998V12.4998C14.202 12.4998 12.5008 14.2012 12.5008 16.2998H12.8008H13.1008C13.1008 14.5325 14.5334 13.0998 16.3008 13.0998V12.7998ZM12.8008 16.2998H12.5008C12.5008 18.3985 14.2021 20.0998 16.3008 20.0998V19.7998V19.4998C14.5334 19.4998 13.1008 18.0671 13.1008 16.2998H12.8008ZM16.3008 19.7998V20.0998C18.3995 20.0998 20.1008 18.3985 20.1008 16.2998H19.8008H19.5008C19.5008 18.0671 18.0681 19.4998 16.3008 19.4998V19.7998ZM19.8008 16.2998H20.1008C20.1008 14.2012 18.3995 12.4998 16.3008 12.4998V12.7998V13.0998C18.0681 13.0998 19.5008 14.5325 19.5008 16.2998H19.8008Z" fill="#2F2B23"/></svg>`;
const PIN_ICON_URL = `data:image/svg+xml;utf8,${encodeURIComponent(PIN_SVG)}`;

/**
 * Cluster colour — matches the Audo design's soft beige. The cluster
 * circle scales with marker count; the count label is rendered with
 * the brand near-black for contrast on the light fill.
 */
const CLUSTER_FILL = '#E5E2D8';
const CLUSTER_FG = '#2F2B23';

function clusterIconUrl(diameter) {
  const r = diameter / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}"><circle cx="${r}" cy="${r}" r="${r}" fill="${CLUSTER_FILL}"/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Default Google Maps style array — light monochrome with the water
 * tinted #c0c0c0 (the design's "backgroundColor"), land slightly
 * lighter, roads paled out and POIs / transit hidden so the map reads
 * as a quiet backdrop to the store pins. The theme setting
 * `google_maps_style_json` overrides this entirely when set.
 */
const DEFAULT_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#d6d6d6' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5a5a5a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#d6d6d6' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#d6d6d6' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c0c0c0' }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const GMAPS_LIBRARIES = 'marker';
let gmapsLoaderPromise = null;

function loadGoogleMaps(apiKey) {
  if (typeof window.google !== 'undefined' && window.google.maps) {
    return Promise.resolve(window.google);
  }
  if (gmapsLoaderPromise) return gmapsLoaderPromise;
  gmapsLoaderPromise = new Promise((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('store-locator: missing Google Maps API key'));
      return;
    }
    const callbackName = `__storeLocatorGmapsReady_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google);
    };
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&libraries=${GMAPS_LIBRARIES}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('store-locator: failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return gmapsLoaderPromise;
}

function loadMarkerClusterer() {
  return import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/@googlemaps/markerclusterer@2.5.3/+esm').catch(
    () => null,
  );
}

// Session-scoped cache key for the visitor's IP-resolved country code so
// we don't hit a geolocation endpoint more than once per session.
const GEO_CODE_CACHE_KEY = 'audoc:geo-country-code';
let geoCountryPromise = null;

// Free IP-geolocation endpoints, tried in order until one yields a
// country code. `pick` pulls the ISO 3166-1 alpha-2 code out of each
// service's own JSON shape.
//   geojs.io — primary: generous free limits, CORS-enabled, no key.
//   ipapi.co — backup: rate-limited (returns 429 once the free quota is
//              spent), so only used if geojs is unreachable.
const GEO_ENDPOINTS = [
  { url: 'https://get.geojs.io/v1/ip/country.json', pick: (d) => d && d.country },
  { url: 'https://ipapi.co/json/', pick: (d) => d && d.country_code },
];

/**
 * Resolve the visitor's ISO 3166-1 alpha-2 country code from their IP.
 * Cached in sessionStorage. Resolves to null when every endpoint fails,
 * so callers fall back to the default (world) view without erroring.
 */
function detectCountryCode() {
  if (geoCountryPromise) return geoCountryPromise;
  let cached = null;
  try {
    cached = sessionStorage.getItem(GEO_CODE_CACHE_KEY);
  } catch {
    // sessionStorage unavailable (private mode / blocked) — ignore.
  }
  // Only a non-empty cached value is a hit. A blank (or missing) value
  // means we never succeeded, so retry rather than stay stuck on null.
  if (cached) {
    geoCountryPromise = Promise.resolve(cached);
    return geoCountryPromise;
  }
  geoCountryPromise = resolveCountryCode();
  return geoCountryPromise;
}

async function resolveCountryCode() {
  for (const { url, pick } of GEO_ENDPOINTS) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) continue;
      const data = await resp.json();
      const raw = pick(data);
      const code = raw ? String(raw).trim().toUpperCase() : '';
      if (code) {
        // Cache only a real result — never a failure — so a transient
        // outage on one load can't suppress detection for the session.
        try {
          sessionStorage.setItem(GEO_CODE_CACHE_KEY, code);
        } catch {
          // Non-fatal — we just won't have a cached value next time.
        }
        return code;
      }
    } catch {
      // Network / CORS error for this endpoint — fall through to the next.
    }
  }
  return null;
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str) {
  return escapeHtml(str);
}

function safeUrl(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:|^tel:/i.test(trimmed)) return trimmed;
  if (/^\/\//.test(trimmed)) return 'https:' + trimmed;
  return 'https://' + trimmed;
}

function addressLine(store) {
  // Match the Audo prototype: each component is its own ` | `-separated
  // segment, except city + postal which join with a comma. State /
  // province (when present) keeps its own segment.
  const cityPostal = [store.city, store.postal].filter(Boolean).join(', ');
  return [store.address1, store.address2, store.state, cityPostal, store.country]
    .filter(Boolean)
    .join(' | ');
}

function directionsHref(store) {
  const q = [store.address1, store.address2, store.city, store.postal, store.country].filter(Boolean).join(', ');
  if (Number.isFinite(store.lat) && Number.isFinite(store.lng) && (store.lat || store.lng)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
}

function storeTypeLabels(store, i18n) {
  const out = [];
  if (store.flags.store) out.push(i18n.type_labels.store);
  if (store.flags.online_shop) out.push(i18n.type_labels.online_store);
  if (store.flags.showroom) out.push(i18n.type_labels.showroom);
  if (store.flags.contract_dealer) out.push(i18n.type_labels.contract_dealer);
  return out.join(', ');
}

class StoreLocator extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    try {
      this.stores = JSON.parse(this.querySelector('script[data-stores]').textContent);
    } catch (err) {
      console.error('store-locator: invalid stores JSON', err);
      this.stores = [];
    }
    try {
      this.i18n = JSON.parse(this.querySelector('script[data-i18n]').textContent);
    } catch (err) {
      console.error('store-locator: invalid i18n JSON', err);
      this.i18n = { tabs: {}, type_labels: {}, regions: {} };
    }

    // Shareable URL state: ?tab=...&store=... lets a user send a
    // specific view (tab + highlighted store) to someone else.
    const urlState = this._readUrlState();
    const defaultTab = this.dataset.defaultTab || 'stores';

    // When the visitor arrives with an explicit tab/store in the URL, or
    // once they interact, we must not override their view with the
    // IP-based region auto-zoom (see _maybeApplyGeoRegion).
    this._hasExplicitInitialView = Boolean(urlState.tab || urlState.storeId);
    this._userInteracted = false;
    this.state = {
      tab: urlState.tab || defaultTab,
      // Multi-select: empty Set is equivalent to "All regions". A
      // store matches when its region key is in the Set (or Set is
      // empty, in which case no filter is applied).
      regions: new Set(),
      query: '',
      activeId: urlState.storeId || null,
    };

    // If a store is in the URL but no tab is, switch to the first
    // tab that store actually appears in so the share link "just
    // works" without requiring the sender to know the tab too.
    if (urlState.storeId && !urlState.tab) {
      const target = this.stores.find((s) => s.id === urlState.storeId);
      if (target) {
        const tabForFlag = { store: 'stores', online_shop: 'online_stores', showroom: 'showrooms', contract_dealer: 'contract_dealers' };
        for (const [flag, tab] of Object.entries(tabForFlag)) {
          if (target.flags && target.flags[flag]) {
            this.state.tab = tab;
            break;
          }
        }
      }
    }

    // Never land on a tab whose category has no stores (e.g. a stale
    // URL or default pointing at an empty category) — its tab is hidden,
    // so fall back to the first populated category.
    const populatedTabs = this._populatedTabs();
    if (populatedTabs.length && !populatedTabs.includes(this.state.tab)) {
      this.state.tab = populatedTabs[0];
    }

    this.activeRegions = this._collectActiveRegions();

    this.chrome = this.querySelector('[data-chrome]');
    this.chrome.innerHTML = this._chromeMarkup();

    this.refs = {
      tabs: this.chrome.querySelector('[data-tabs]'),
      regions: this.chrome.querySelector('[data-regions]'),
      searchInput: this.chrome.querySelector('[data-search]'),
      list: this.chrome.querySelector('[data-list]'),
      mapWrap: this.chrome.querySelector('[data-map-wrap]'),
      mapEl: this.chrome.querySelector('[data-map]'),
    };

    this._applyMapHeights();
    this._bindEvents();
    this._renderTabs();
    this._renderRegions();
    this._renderList();
    this._toggleMapVisibility();
    this._reflectTab();

    const apiKey = this.dataset.gmapsKey;
    if (apiKey) {
      loadGoogleMaps(apiKey)
        .then((google) => this._initMap(google))
        .catch((err) => {
          console.error(err);
          if (this.refs.mapWrap) this.refs.mapWrap.classList.add('is-map-failed');
        });
    } else {
      console.warn('store-locator: no Google Maps API key set in theme settings');
    }

    // Start the map zoomed in on the visiting user's region. We resolve
    // their country from their IP and pre-select the matching region so
    // the initial fitBounds frames nearby stores rather than the whole
    // world. Fully best-effort: any failure leaves the default view.
    detectCountryCode().then((code) => {
      if (!code) return;
      this._maybeApplyGeoRegion(regionForCountryCode(code));
    });

    // If the URL pre-selected a store, scroll / pan to it once the
    // initial list (and, when available, the map) has rendered.
    if (this.state.activeId) {
      requestAnimationFrame(() => {
        this._setActive(this.state.activeId, { scroll: true, pan: true });
      });
    }
  }

  // Auto-select the visitor's region so the map opens zoomed in on it.
  // Deliberately conservative: it bails the moment the visitor has their
  // own view (URL-pinned tab/store, a manual interaction, or an already
  // non-empty region filter), and only applies to a region that actually
  // has stores in the current tab — otherwise the auto-zoom would land
  // the visitor on an empty "no results" list.
  _maybeApplyGeoRegion(regionKey) {
    if (this._hasExplicitInitialView || this._userInteracted) return;
    if (this.state.regions.size !== 0) return;
    // `rest_of_world` spans the globe, so zooming to it is no better than
    // the default all-stores view — leave the default in that case.
    if (!regionKey || regionKey === 'rest_of_world') return;
    if (!this.activeRegions.includes(regionKey)) return;
    if (!this._availableRegionsForCurrentTab().has(regionKey)) return;

    this.state.regions = new Set([regionKey]);
    this._renderRegions();
    // Re-render the list, which re-syncs markers and fitBounds() to the
    // region. If the map isn't ready yet, its init reads this filtered
    // state and frames the region as soon as the markers are built.
    this._renderList();
  }

  _readUrlState() {
    try {
      const params = new URLSearchParams(window.location.search);
      return { tab: params.get('tab'), storeId: params.get('store') };
    } catch {
      return { tab: null, storeId: null };
    }
  }

  _writeUrlState() {
    try {
      const params = new URLSearchParams(window.location.search);
      const defaultTab = this.dataset.defaultTab || 'stores';
      if (this.state.tab && this.state.tab !== defaultTab) {
        params.set('tab', this.state.tab);
      } else {
        params.delete('tab');
      }
      if (this.state.activeId) {
        params.set('store', this.state.activeId);
      } else {
        params.delete('store');
      }
      const qs = params.toString();
      const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    } catch {
      // History API not available; silently no-op.
    }
  }

  _collectActiveRegions() {
    const seen = new Set(this.stores.map((s) => s.region).filter(Boolean));
    return REGION_KEYS.filter((k) => seen.has(k));
  }

  _availableRegionsForCurrentTab() {
    // Set of region keys that have at least one store matching the
    // current tab's flag. Drives which region pills are enabled.
    const flag = TAB_TO_FLAG[this.state.tab];
    const seen = new Set();
    for (const s of this.stores) {
      if (s.flags && s.flags[flag] && s.region) seen.add(s.region);
    }
    return seen;
  }

  _applyMapHeights() {
    const m = Number(this.dataset.mobileMapHeight) || 360;
    this.style.setProperty('--store-locator-map-height-desktop', 'calc(100vh - (96px + var(--header-height) + var(--padding-top)))');
    this.style.setProperty('--store-locator-map-height-mobile', `${m}px`);
  }

  _chromeMarkup() {
    const showSearch = this.dataset.showSearch === 'true';
    const showRegionTabs = this.dataset.showRegionTabs === 'true';
    // The whole UI lives inside the body now so the overlay layout
    // (Stores / Contract Dealers) can position the panel + regions
    // freely on top of the map via CSS. For the non-map tabs the
    // same DOM falls back to normal flow.
    return `
      <div class="store-locator__body">
        ${showRegionTabs ? '<div class="store-locator__regions" data-regions></div>' : ''}
        <div class="store-locator__map-wrap" data-map-wrap>
          <div class="store-locator__map" data-map></div>
        </div>
        <div class="store-locator__panel">
          <div class="store-locator__tab-bar">
            <div class="store-locator__tabs-wrap">
              <div class="store-locator__tabs" data-tabs role="tablist"></div>
            </div>
            <div class="store-locator__filter-bar">
              <div class="store-locator__tab-bar-regions" data-regions-toggle-slot></div>
            </div>
          </div>
          ${
            showSearch
              ? `<div class="store-locator__search">
                  <input type="search" data-search class="store-locator__search-input"
                    placeholder="${escapeAttr(this.i18n.search_placeholder)}"
                    aria-label="${escapeAttr(this.i18n.search_placeholder)}">
                </div>`
              : ''
          }
          <ul class="store-locator__list" data-list role="list"></ul>
        </div>
      </div>
    `;
  }

  // Whether a category has any stores at all — deliberately ignores the
  // active region filter and search query, so a tab is only hidden when
  // the category is genuinely empty, not merely filtered to nothing.
  _tabHasStores(key) {
    const flag = TAB_TO_FLAG[key];
    return this.stores.some((s) => s.flags && s.flags[flag]);
  }

  // The four categories in fixed order, minus any that have no stores.
  _populatedTabs() {
    return ['stores', 'online_stores', 'showrooms', 'contract_dealers'].filter((key) =>
      this._tabHasStores(key),
    );
  }

  _renderTabs() {
    const tabKeys = this._populatedTabs();
    this.refs.tabs.innerHTML = tabKeys
      .map(
        (key) => `
          <button type="button"
            class="store-locator__tab ${key === this.state.tab ? 'is-active' : ''}"
            role="tab"
            aria-selected="${key === this.state.tab}"
            data-tab="${key}">${escapeHtml(this.i18n.tabs[key] || key)}</button>
        `,
      )
      .join('');
  }

  _renderRegions() {
    if (!this.refs.regions) return;
    const keys = ['all', ...this.activeRegions];
    const available = this._availableRegionsForCurrentTab();
    const isActive = (key) =>
      key === 'all' ? this.state.regions.size === 0 : this.state.regions.has(key);
    // "All regions" is always enabled; everything else needs at least
    // one matching store under the current tab.
    const isDisabled = (key) => key !== 'all' && !available.has(key);
    const pills = keys
      .map(
        (key) => `
          <button type="button"
            class="store-locator__region ${isActive(key) ? 'is-active' : ''} ${isDisabled(key) ? 'is-disabled' : ''}"
            data-region="${key}"
            ${isDisabled(key) ? 'disabled aria-disabled="true"' : ''}>${escapeHtml(this.i18n.regions[key] || key)}</button>
        `,
      )
      .join('');

    // Non-map tabs: render the "Regions (N) ⌄" trigger into the
    // tab-bar slot, with the pill list collapsing below the tab-bar.
    // Map tabs: keep the inline pill row inside the regions element
    // and clear the tab-bar slot.
    const isMapTab = MAP_TABS.has(this.state.tab);
    const toggleSlot = this.chrome.querySelector('[data-regions-toggle-slot]');

    if (isMapTab) {
      this.refs.regions.innerHTML = pills;
      this.refs.regions.removeAttribute('data-expanded');
      if (toggleSlot) toggleSlot.innerHTML = '';
    } else {
      // Selected count: the number of explicitly chosen region keys
      // (0 when the user is on "All regions").
      const count = this.state.regions.size;
      const label = this.i18n.regions_label || 'Regions';
      // Default to open on first render (no attribute yet), but only
      // on desktop — mobile starts collapsed. On re-renders preserve
      // whatever the user toggled it to.
      const wasExpanded = this.refs.regions.hasAttribute('data-expanded')
        ? this.refs.regions.getAttribute('data-expanded') === 'true'
        : window.matchMedia('(min-width: 990px)').matches;
      if (toggleSlot) {
        toggleSlot.innerHTML = `
          <button type="button" class="store-locator__regions-toggle" data-regions-toggle
                  aria-expanded="${wasExpanded ? 'true' : 'false'}">
            <span>${escapeHtml(label)} (${count})</span>
          </button>
        `;
      }
      this.refs.regions.innerHTML = `<div class="store-locator__regions-list" data-regions-list>${pills}</div>`;
      this.refs.regions.setAttribute('data-expanded', wasExpanded ? 'true' : 'false');
    }
  }

  _bindEvents() {
    this.refs.tabs.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tab]');
      if (!btn) return;
      this._userInteracted = true;
      this.state.tab = btn.dataset.tab;
      // Drop the active store on tab switch — it may not exist in
      // the new tab's filter, and the URL shouldn't pin a phantom.
      this.state.activeId = null;
      // Prune any previously-selected regions that have no matching
      // stores in the new tab — leaving them in the Set would silently
      // hide stores in regions the user can no longer toggle off.
      const available = this._availableRegionsForCurrentTab();
      for (const key of [...this.state.regions]) {
        if (!available.has(key)) this.state.regions.delete(key);
      }
      this._renderTabs();
      // Re-render regions so the toggle/inline-pill split matches the
      // new tab (map view = inline pills, non-map = collapsible).
      this._renderRegions();
      this._toggleMapVisibility();
      this._renderList();
      this._reflectTab();
      this._writeUrlState();
    });

    // Regions: the toggle lives in the tab-bar slot, the pills live
    // inside `this.refs.regions`. A single chrome-level delegate
    // catches both so we don't need two listeners.
    this.chrome.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-regions-toggle]');
      if (toggle && this.refs.regions) {
        const expanded = this.refs.regions.getAttribute('data-expanded') === 'true';
        this.refs.regions.setAttribute('data-expanded', String(!expanded));
        toggle.setAttribute('aria-expanded', String(!expanded));
        return;
      }
      const btn = e.target.closest('[data-region]');
      if (!btn) return;
      this._userInteracted = true;
      const key = btn.dataset.region;
      if (key === 'all') {
        // "All regions" clears the filter set (i.e. show every store).
        this.state.regions.clear();
      } else if (this.state.regions.has(key)) {
        this.state.regions.delete(key);
      } else {
        this.state.regions.add(key);
      }
      this._renderRegions();
      this._renderList();
    });

    if (this.refs.searchInput) {
      const onSearch = debounce(() => {
        this._userInteracted = true;
        this.state.query = this.refs.searchInput.value.trim().toLowerCase();
        this._renderList();
      }, 150);
      this.refs.searchInput.addEventListener('input', onSearch);
    }

    this.refs.list.addEventListener('click', (e) => {
      // Cards are only selectable when the map is active — selecting a
      // card pans the map, which has no effect on the map-less tabs.
      if (!MAP_TABS.has(this.state.tab)) return;
      // Allow clicks on internal links/buttons to behave normally
      if (e.target.closest('a, button')) return;
      const card = e.target.closest('[data-store-id]');
      if (!card) return;
      this._userInteracted = true;
      this._setActive(card.dataset.storeId, { pan: true });
    });
  }

  _reflectTab() {
    // Surface the active tab to CSS so per-tab layout rules can
    // target it (e.g. the Online Stores grid vs Showrooms row layout).
    this.dataset.activeTab = this.state.tab;
    this._positionRegions();
  }

  _positionRegions() {
    // Map tabs: regions floats top-right of the body, overlaying the
    // map (handled by the desktop grid CSS).
    // Non-map tabs: regions sits between the tab-bar and the search
    // inside the panel, in the natural document flow.
    const regions = this.refs.regions;
    if (!regions) return;
    const body = this.chrome.querySelector('.store-locator__body');
    const panel = this.chrome.querySelector('.store-locator__panel');
    const tabBar = this.chrome.querySelector('.store-locator__tab-bar');
    if (!body || !panel || !tabBar) return;

    const isMapTab = MAP_TABS.has(this.state.tab);
    if (isMapTab) {
      // Regions should be a direct child of body for the overlay layout.
      if (regions.parentElement !== body) body.insertBefore(regions, body.firstElementChild);
    } else {
      // Regions should sit inside the panel, immediately after the tab-bar.
      const after = tabBar.nextElementSibling;
      if (regions.parentElement !== panel || regions !== after) {
        panel.insertBefore(regions, tabBar.nextElementSibling);
      }
    }
  }

  _toggleMapVisibility() {
    const show = MAP_TABS.has(this.state.tab);
    this.refs.mapWrap.hidden = !show;
    this.refs.mapWrap.classList.toggle('is-hidden', !show);
    if (show && this.map) {
      // Trigger a resize so the map redraws after being un-hidden
      window.google?.maps?.event?.trigger(this.map, 'resize');
    }
  }

  _filtered() {
    const flag = TAB_TO_FLAG[this.state.tab];
    // Ignore the query on tabs without a search field (Showrooms) so a
    // query from another tab doesn't filter results the user can't clear.
    const q = SEARCH_TABS.has(this.state.tab) ? this.state.query : '';
    return this.stores
      .filter((s) => s.flags && s.flags[flag])
      .filter((s) => this.state.regions.size === 0 || this.state.regions.has(s.region))
      .filter((s) => {
        if (!q) return true;
        return this._searchHaystack(s).includes(q);
      })
      // Fixed A–Z ordering — the sort dropdown was removed per client
      // feedback, so the list is always alphabetical.
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }

  // Lowercased blob of every searchable field for a store: name, full
  // address, phone, email and website. Built once per store and cached
  // on the record so repeated keystrokes don't rebuild it for all ~1000
  // stores each time.
  _searchHaystack(s) {
    if (s._haystack === undefined) {
      s._haystack = [
        s.name,
        s.address1,
        s.address2,
        s.city,
        s.state,
        s.postal,
        s.country,
        s.phone,
        s.email,
        s.website,
        s.online_shop_link,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    }
    return s._haystack;
  }

  _renderList() {
    const filtered = this._filtered();
    // Markers always follow the tab/region/search filter. The card list
    // is additionally narrowed to the map viewport on the map tab, so
    // panning/zooming surfaces only the stores in view.
    const willReframe = this._syncMarkers(filtered);
    // When _syncMarkers is about to fitBounds (the visible set changed),
    // the current viewport is stale — render the full set now and let the
    // map's 'idle' event narrow the list once the reframe settles.
    this._renderCards(willReframe ? filtered : this._inView(filtered));
  }

  // Render the card DOM only — marker sync is handled separately so the
  // viewport-driven re-renders (map 'idle') can't feed back into
  // fitBounds and re-trigger themselves.
  _renderCards(list) {
    if (!list.length) {
      this.refs.list.innerHTML = `<li class="store-locator__empty">${escapeHtml(this.i18n.no_results)}</li>`;
    } else {
      const renderer = SHOWROOM_TABS.has(this.state.tab) ? this._renderShowroomCard : this._renderStoreCard;
      this.refs.list.innerHTML = list.map((s) => renderer.call(this, s)).join('');
    }
    // Re-apply the active highlight — innerHTML replacement dropped it.
    if (this.state.activeId) {
      const active = this.refs.list.querySelector(`[data-store-id="${CSS.escape(this.state.activeId)}"]`);
      if (active) active.classList.add('is-active');
    }
  }

  // Narrow a filtered list to the stores inside the current map
  // viewport. Only applies on the map tab once bounds exist; stores
  // without coordinates have no pin to find, so they stay listed
  // regardless of viewport to remain discoverable.
  _inView(list) {
    if (!MAP_TABS.has(this.state.tab) || !this.map) return list;
    const bounds = this.map.getBounds();
    if (!bounds) return list;
    return list.filter((s) => {
      if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) return true;
      if (!s.lat && !s.lng) return true;
      return bounds.contains({ lat: s.lat, lng: s.lng });
    });
  }

  _renderStoreCard(s) {
    const labels = storeTypeLabels(s, this.i18n);
    const address = escapeHtml(addressLine(s));
    const website = safeUrl(s.website || s.online_shop_link);
    // Online Stores only: the name doubles as a link to the shop, so the
    // whole title is clickable and not just the "Website" CTA. Physical
    // store cards keep a plain name — per client feedback they already
    // carry two CTAs (website + directions).
    const nameHtml =
      website && this.state.tab === 'online_stores'
        ? `<a href="${escapeAttr(website)}" target="_blank" rel="noopener noreferrer" class="store-locator__card-name-link">${escapeHtml(s.name)}</a>`
        : escapeHtml(s.name);
    return `
      <li class="store-locator__card" data-store-id="${escapeAttr(s.id)}">
        <div class="store-locator__card-header">
          ${labels ? `<p class="store-locator__card-types">${escapeHtml(labels)}</p>` : ''}
          <h3 class="store-locator__card-name tw-font-primary">${nameHtml}</h3>
          <p class="store-locator__card-address">${address}</p>
        </div>
        <div class="store-locator__card-foot">
          <div class="store-locator__card-contact">
            ${s.phone ? `<p>${escapeHtml(s.phone)}</p>` : ''}
            ${s.email ? `<p><a href="mailto:${escapeAttr(s.email)}">${escapeHtml(s.email)}</a></p>` : ''}
          </div>
          <div class="store-locator__card-actions">
            ${
              website
                ? `<a href="${escapeAttr(website)}" target="_blank" rel="noopener noreferrer" class="store-locator__link website">${escapeHtml(this.i18n.website)}</a>`
                : ''
            }
            <a href="${escapeAttr(directionsHref(s))}" target="_blank" rel="noopener noreferrer" class="store-locator__link directions">${escapeHtml(this.i18n.directions)}</a>
          </div>
        </div>
      </li>
    `;
  }

  _renderShowroomCard(s) {
    const labels = storeTypeLabels(s, this.i18n);
    const address = escapeHtml(addressLine(s));
    const learn = safeUrl(s.learn_more_url);
    const images = Array.isArray(s.images) ? s.images : [];
    return `
      <li class="store-locator__card store-locator__card--showroom" data-store-id="${escapeAttr(s.id)}">
        <div class="store-locator__card-body">
          <div class="store-locator__card-content">
            ${labels ? `<p class="store-locator__card-types">${escapeHtml(labels)}</p>` : ''}
            <h3 class="store-locator__card-name">${escapeHtml(s.name)}</h3>
            ${s.description ? `<div class="store-locator__card-description rte">${s.description}</div>` : ''}
            <p class="store-locator__card-address">${address}</p>
            <p class="store-locator__card-actions">
              ${
                learn
                  ? `<a href="${escapeAttr(learn)}" target="_blank" rel="noopener noreferrer" class="store-locator__link learn_more">${escapeHtml(this.i18n.learn_more)}</a>`
                  : ''
              }
              <a href="${escapeAttr(directionsHref(s))}" target="_blank" rel="noopener noreferrer" class="store-locator__link directions">${escapeHtml(this.i18n.directions)}</a>
            </p>
          </div>
          ${
            s.phone || s.email || s.opening_hours
              ? `<div class="store-locator__card-details">
            ${
              s.phone || s.email
                ? `<div>
              <p class="store-locator__card-details-label">${escapeHtml(this.i18n.contact_details)}</p>
              ${s.phone ? `<p>${escapeHtml(s.phone)}</p>` : ''}
              ${s.email ? `<p><a href="mailto:${escapeAttr(s.email)}">${escapeHtml(s.email)}</a></p>` : ''}
            </div>`
                : ''
            }
            ${
              s.opening_hours
                ? `<div>
                    <p class="store-locator__card-details-label">${escapeHtml(this.i18n.opening_hours)}</p>
                    <p class="store-locator__card-hours">${escapeHtml(s.opening_hours)}</p>
                  </div>`
                : ''
            }
          </div>`
              : ''
          }
        </div>
        ${(() => {
          const total = images.length;
          if (!total) return '';
          // The reachable range of `current` is [0, max(total-2, 0)].
          // Layout states drive CSS via [data-state]:
          //   single  — gallery has exactly 1 image
          //   start   — current = 0 (no left peek)
          //   mid     — 0 < current < total-2 (left peek + 2 right peeks)
          //   end     — current = total-2 (50/50, no scroll forward)
          const initialState = total === 1 ? 'single' : total === 2 ? 'end' : 'start';
          const nextArrow =  `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.164 7.518H0V8.476H14.17L7.323 15.323L8 16L16 8L8 0L7.323 0.677L14.164 7.518Z" fill="#F5F3EB"/></svg>`;
          const prevArrow = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.836 7.518H16V8.476H1.83L8.677 15.323L8 16L0 8L8 0L8.677 0.677L1.836 7.518Z" fill="#F5F3EB"/></svg>`;
          const showButtons = total > 2;
          return `<div class="store-locator__slider"
            data-slider
            data-current="0"
            data-image-count="${total}"
            data-state="${initialState}"
            data-at-start="true"
            data-at-end="${total <= 2 ? 'true' : 'false'}">
            <div class="store-locator__slider-track">
              ${images
                .map(
                  (src, i) =>
                    `<img src="${escapeAttr(src)}" alt="${escapeAttr(s.name)} ${i + 1}" loading="lazy" decoding="async" data-offset="${i}" class="${i === 0 ? 'is-active' : ''}">`,
                )
                .join('')}
            </div>
            ${
              showButtons
                ? `<button type="button" class="store-locator__slider-btn store-locator__slider-btn--prev" data-slider-prev aria-label="Previous">${prevArrow}</button>
                   <button type="button" class="store-locator__slider-btn store-locator__slider-btn--next" data-slider-next aria-label="Next">${nextArrow}</button>`
                : ''
            }
          </div>`;
        })()}
      </li>
    `;
  }

  _initMap(google) {
    this.map = new google.maps.Map(this.refs.mapEl, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative',
      styles: this._parseMapStyles(),
    });

    // Keep the list in step with the viewport: once any pan/zoom
    // settles, narrow the cards to the stores currently in view.
    // Markers are deliberately NOT re-synced here — they follow the
    // tab/region/search filter only, so a viewport change can never
    // re-trigger fitBounds and loop back into another 'idle'.
    this.map.addListener('idle', () => {
      if (!MAP_TABS.has(this.state.tab)) return;
      this._renderCards(this._inView(this._filtered()));
    });

    // Build every marker exactly once, keyed by store id. Filtering and
    // search then only toggle which of these existing markers the
    // clusterer holds — markers are never reconstructed. Rebuilding
    // 500+ markers on every keystroke was the main source of lag.
    this.markers = new Map();
    const icon = {
      url: PIN_ICON_URL,
      // Native size (33×41). The anchor pins the SVG's tip to the
      // lat/lng; bottom-centre of the viewbox. Shared across all markers
      // so we don't allocate an icon object per pin.
      size: new google.maps.Size(33, 41),
      scaledSize: new google.maps.Size(33, 41),
      anchor: new google.maps.Point(16.5, 41),
    };
    for (const s of this.stores) {
      if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
      if (!s.lat && !s.lng) continue;
      const marker = new google.maps.Marker({
        position: { lat: s.lat, lng: s.lng },
        title: s.name,
        icon,
      });
      marker.addListener('click', () => this._setActive(s.id, { pan: true, scroll: true }));
      this.markers.set(s.id, marker);
    }

    // Create one clusterer up front and reuse it for the element's
    // lifetime. Creating a new clusterer per render (the old behaviour)
    // leaked map idle/zoom listeners, so every pan compounded the cost.
    loadMarkerClusterer().then((mod) => {
      if (mod && mod.MarkerClusterer) {
        this.cluster = new mod.MarkerClusterer({
          map: this.map,
          markers: [],
          renderer: {
            // Each cluster gets a beige circle sized by member count.
            // Diameter scales gently from 36px → 60px so dense clusters
            // read as visually heavier without overpowering the map.
            render: ({ count, position }) => {
              const diameter = Math.min(60, 36 + Math.round(Math.log2(count + 1) * 6));
              return new google.maps.Marker({
                position,
                icon: {
                  url: clusterIconUrl(diameter),
                  size: new google.maps.Size(diameter, diameter),
                  scaledSize: new google.maps.Size(diameter, diameter),
                  anchor: new google.maps.Point(diameter / 2, diameter / 2),
                },
                label: {
                  text: String(count),
                  color: CLUSTER_FG,
                  fontSize: '12px',
                  fontWeight: '500',
                },
                // Keep clusters above individual pins.
                zIndex: 1000 + count,
              });
            },
          },
        });
      }
      // Markers + clusterer are ready; populate for the current view.
      this._mapReady = true;
      this._markerKey = null;
      this._syncMarkers(this._filtered());
    });
  }

  _parseMapStyles() {
    const raw = this.dataset.gmapsStyle;
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.warn('store-locator: invalid map style JSON, falling back to default', e);
      }
    }
    return DEFAULT_MAP_STYLES;
  }

  // Returns true when the marker set changed and the map is being
  // reframed with fitBounds — callers use this to defer viewport-based
  // list narrowing until the move settles ('idle').
  _syncMarkers(list) {
    if (!this._mapReady || !this.map || !window.google || !this.markers) return false;

    const google = window.google;
    const showMap = MAP_TABS.has(this.state.tab);

    // Ids of stores that are both visible under the current filter and
    // have a real marker (valid coords). Ids are sorted into the key so
    // an order-only change in the list is a no-op.
    const ids = showMap ? list.filter((s) => this.markers.has(s.id)).map((s) => s.id) : [];
    const key = this.state.tab + '|' + [...ids].sort().join(',');
    if (key === this._markerKey) return false;
    this._markerKey = key;

    const subset = ids.map((id) => this.markers.get(id));

    if (this.cluster) {
      // Reuse the single clusterer: swap its marker set instead of
      // rebuilding markers or the clusterer itself.
      this.cluster.clearMarkers();
      if (subset.length) this.cluster.addMarkers(subset);
    } else {
      // No clusterer available — attach/detach markers directly.
      for (const m of this.markers.values()) m.setMap(null);
      for (const m of subset) m.setMap(this.map);
    }

    if (showMap && subset.length) {
      const bounds = new google.maps.LatLngBounds();
      for (const m of subset) bounds.extend(m.getPosition());
      this.map.fitBounds(bounds, this._focusPadding());
      return true;
    }
    return false;
  }

  _focusPadding() {
    // Push the bounds away from the area covered by the panel so the
    // markers settle in the uncovered region of the map.
    if (window.matchMedia('(min-width: 990px)').matches) {
      return {
        top: 32,
        right: 32,
        bottom: 32,
        left: PANEL_DESKTOP_WIDTH + PANEL_DESKTOP_MARGIN * 2,
      };
    }
    const mapHeight = this.refs.mapEl?.clientHeight || 0;
    const bottomCovered = Math.max(0, mapHeight - PANEL_MOBILE_VISIBLE_TOP);
    return { top: 16, right: 16, bottom: bottomCovered + 16, left: 16 };
  }

  _focusOffsetPx() {
    // Pixel delta to apply via panBy AFTER panTo(marker), so the
    // marker visually lands in the uncovered area instead of behind
    // the panel. Signs follow Google Maps' convention: positive x is
    // east, positive y is south (panning the centre in those
    // directions makes the marker appear in the opposite direction).
    if (window.matchMedia('(min-width: 990px)').matches) {
      // Shift centre west so marker appears in the right portion.
      return { x: -(PANEL_DESKTOP_WIDTH / 2 + PANEL_DESKTOP_MARGIN), y: 0 };
    }
    const mapHeight = this.refs.mapEl?.clientHeight || 0;
    const bottomCovered = Math.max(0, mapHeight - PANEL_MOBILE_VISIBLE_TOP);
    // Shift centre south so marker appears in the top portion.
    return { x: 0, y: bottomCovered / 2 };
  }

  _setActive(id, { pan = false, scroll = false } = {}) {
    // Any explicit store selection (card or map marker) counts as the
    // visitor taking control, so the IP-based auto-zoom won't override it.
    this._userInteracted = true;
    this.state.activeId = id;
    this.refs.list.querySelectorAll('[data-store-id]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.storeId === id);
    });
    const card = this.refs.list.querySelector(`[data-store-id="${CSS.escape(id)}"]`);
    if (card && scroll) {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    const marker = this.markers && this.markers.get(id);
    if (marker && pan && this.map) {
      this.map.panTo(marker.getPosition());
      if (this.map.getZoom() < FOCUS_ZOOM) this.map.setZoom(FOCUS_ZOOM);
      // After centring on the marker, offset the view so the marker
      // sits inside the uncovered area of the map (right of the
      // desktop panel / above the mobile panel).
      const offset = this._focusOffsetPx();
      if (offset.x || offset.y) this.map.panBy(offset.x, offset.y);
    }
    this._writeUrlState();
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}

/**
 * Showroom image-gallery controller.
 *
 * Desktop behaviour (per design): a single "active" image fills ~60%
 * of the gallery while the rest sit as thin peek strips on the right.
 * Clicking next/prev animates the active class — the CSS handles the
 * flex-basis transition. Buttons hide at the gallery's bounds.
 *
 * Mobile behaviour: full-width scroll-snap (active class is moved in
 * lockstep so the state stays consistent across breakpoints).
 *
 * Delegated so cards re-rendered on filter still get controls.
 */
document.addEventListener('click', (e) => {
  const prevBtn = e.target.closest('[data-slider-prev]');
  const nextBtn = e.target.closest('[data-slider-next]');
  if (!prevBtn && !nextBtn) return;
  const slider = (prevBtn || nextBtn).closest('[data-slider]');
  if (!slider) return;
  const track = slider.querySelector('.store-locator__slider-track');
  if (!track) return;
  const imgs = Array.from(track.querySelectorAll('img'));
  if (!imgs.length) return;

  const total = imgs.length;
  // Desktop renders the final reachable position as a balanced 50/50
  // view of the last two images, so it stops at total-2. Mobile shows
  // one slide at a time, so the last reachable slide is the actual last
  // image (total-1) — otherwise the next arrow vanishes a slide early
  // and the last image is only reachable by swiping.
  const isMobile = window.matchMedia('(max-width: 989px)').matches;
  const maxCurrent = Math.max(0, total - (isMobile ? 1 : 2));
  const current = Number(slider.dataset.current || 0);
  const target = current + (nextBtn ? 1 : -1);
  if (target < 0 || target > maxCurrent) return;

  // Compute the visual state CSS targets via [data-state]:
  //   single (1 image), start (current=0), end (current=maxCurrent),
  //   mid (anywhere in between).
  let state;
  if (total === 1) state = 'single';
  else if (total === 2 || target === maxCurrent) state = 'end';
  else if (target === 0) state = 'start';
  else state = 'mid';

  slider.dataset.current = String(target);
  slider.dataset.state = state;
  slider.dataset.atStart = target === 0 ? 'true' : 'false';
  slider.dataset.atEnd = target >= maxCurrent ? 'true' : 'false';

  imgs.forEach((img, i) => {
    const offset = i - target;
    img.dataset.offset = String(offset);
    img.classList.toggle('is-active', offset === 0);
  });

  // Mobile uses scroll-snap; keep the visible slide in sync with state.
  if (window.matchMedia('(max-width: 989px)').matches) {
    const step = imgs[target].getBoundingClientRect().width || track.clientWidth;
    track.scrollTo({ left: target * step, behavior: 'smooth' });
  }
});
